const CACHE_DURATION: number = 8 * 60 * 60 * 1000; // 8 hours
const CACHE_KEY_PREFIX: string = 'bm.locale_cache_';
const VERSION: string = import.meta.env.VITE_VERSION || 'unknown';
const IS_DEV_VERSION: boolean = VERSION === 'dev' || VERSION === 'local';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const generateCacheKey = (lang: string): string =>
  `${CACHE_KEY_PREFIX}${lang}_${VERSION}`;

const cleanupOldCacheEntries = (): void => {
  if (IS_DEV_VERSION) return;

  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.error('Error cleaning up old cache entries:', error);
  }
};

export const getFromCache = <T>(lang: string): T | null => {
  if (IS_DEV_VERSION) return null;

  const cacheKey = generateCacheKey(lang);

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached) as CacheEntry<T>;
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      } else {
        localStorage.removeItem(cacheKey);
      }
    }
  } catch (error) {
    console.error(`Error reading cache for ${lang}:`, error);
    try {
      localStorage.removeItem(cacheKey);
    } catch (err) {
      console.error(`Error removing corrupted cache for ${lang}:`, err);
    }
  }

  return null;
};

export const setToCache = <T>(lang: string, data: T): void => {
  if (IS_DEV_VERSION) {
    return;
  }

  cleanupOldCacheEntries();
  const cacheKey = generateCacheKey(lang);

  try {
    localStorage.setItem(
      cacheKey,
      JSON.stringify({
        data,
        timestamp: Date.now()
      } satisfies CacheEntry<T>)
    );
  } catch (error) {
    console.error(`Error caching ${lang}:`, error);
  }
};
